import React, { useState, useEffect } from 'react';
import './App.css';
import './styles.css';
import Login from './components/common/Login';
import Modal from './components/common/Modal';
import ContextDropdown from './components/input/ContextDropdown';
import ConceptsDropdown from './components/input/ConceptsDropdown';
import CodeEditor from './components/code/CodeEditor';
import ConsoleOutput from './components/code/ConsoleOutput';
import TestResults from './components/code/TestResults';
import TestResultCircles from './components/code/TestResultCircles';
import { HandleProblem } from './services/HandleProblem';
import { ExecuteCode } from './hooks/useCodeExecution';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { RocketLaunchIcon, CodeBracketIcon, CommandLineIcon } from '@heroicons/react/24/outline'
import { AwesomeButton } from 'react-awesome-button';
import "react-awesome-button/dist/styles.css";
import "./awesome-button-custom.css";
import {FeedbackForm} from './components/feedback/FeedbackForm';
import { useFeedback } from './hooks/useFeedback';
import { LoginService } from './services/Login';
import { BASE_URL } from './utils/constants';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BrowserRouter as Router } from 'react-router-dom';


interface CodeProps extends React.ComponentPropsWithoutRef<'code'> {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

interface TestResult {
  passed: boolean;
  message: string;
}

interface User {
  user_id: number;
  email: string;
  is_study_participant: boolean;
}

interface FeedbackData {
  user: number;
  problem: number;
  context_satisfaction: number;
  concepts_satisfaction: number;
  comprehensible: number;
  difficulty: number;
  interestingness: number;
  comments: string;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [user_id, setUser_id] = useState<number | null>(null);
  const [session_id, setSession_id] = useState<string | null>(null);
  const [is_study_participant, setIs_study_participant] = useState<boolean | null>(null);
  const [isInputsDisabled , setIsInputsDisabled] = useState<boolean>(false);
  const [selectedContext, setSelectedContext] = useState('');
  const [selectedConcepts, setSelectedConcepts] = useState<string[]>([]);
  const [problem, setProblem] = useState('');
  const [problemId, setProblemId] = useState<number | null>(null);
  const [code, setCode] = useState('# solution_program.py\n# Your Python code here\n\n\n\n\n\n\n\n\n\n\n\n\n');
  const [test_suite, setTest_suite] = useState('');
  const [consoleOutput, setConsoleOutput] = useState('');
  const [testResults, setTestResults] = useState<any[]>([]);
  // const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isProblemGenerated, setIsProblemGenerated] = useState(false);
  const [isGeneratingProblem, setIsGeneratingProblem] = useState(false);
  const [isExecutingCode, setIsExecutingCode] = useState(false);
  const [inputWarning, setInputWarning] = useState('');
  const [problemPlaceholder, setProblemPlaceholder] = useState('A task will be generated in about 2-3 minutes.');
  const [generateButtonState, setGenerateButtonState] = useState('idle');
  const [generationTimer, setGenerationTimer] = useState<NodeJS.Timeout | null>(null);
  const [hasRunCode, setHasRunCode] = useState(false);

  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Define the handleLogin function
  const handleLogin = (loggedInUser: User) => {
    setUser(loggedInUser);
    setUser_id(loggedInUser.user_id);
    const is_study_participant = localStorage.getItem('is_study_participant');
    setIs_study_participant(is_study_participant === "true");
    const session_id = localStorage.getItem('session_id') || crypto.randomUUID();
    setSession_id(session_id);
    localStorage.setItem('session_id', session_id);
    
    if (is_study_participant === "true") {
      setIsInputsDisabled(true);
    }
    else {
      setIsInputsDisabled(false);
    }
  };

  // Update the useEffect to use LoginService
  useEffect(() => {
    const guestUser = LoginService.handleGuestLogin();
    handleLogin(guestUser);
  }, []);

  useEffect(() => {
    // Debug using both methods
    console.log('Location:', location);
    console.log('Search string:', location.search);
    console.log('SearchParams:', searchParams);
    
    // Try parsing manually as backup
    const urlParams = new URLSearchParams(location.search);
    console.log('Manual parse:', {
      user_id: urlParams.get('uid'),
      problem_id: urlParams.get('pid')
    });
    
    const urlUserId = searchParams.get('uid');
    const urlProblemId = searchParams.get('pid');
  
    if (urlUserId && urlProblemId) {
      setUser_id(parseInt(urlUserId));
      setProblemId(parseInt(urlProblemId));
      setIs_study_participant(true);
      setIsInputsDisabled(true);
      console.log(urlProblemId);
      // Fetch problem details
      fetch(`${BASE_URL}/problem/${urlProblemId}/?format=json`)
        .then(response => response.json())
        .then(async data => {
          console.log(data);
          setSelectedContext(data.context);
          setSelectedConcepts(data.programming_concepts.split(', '));
          setProblem(data.problem_statement);
          setTest_suite(data.test_driver);
          setIsProblemGenerated(true);
        })
        .catch(error => console.error('Error fetching problem:', error));
    }
    
  }, [location, searchParams]);

  useEffect(() => {
    if (isProblemGenerated && test_suite && problemId) {
      handleExecuteCode(false);
    }
  }, [isProblemGenerated, test_suite, problemId]);

  const handleProblemStatusSuccess = (status: any) => {
    if (generationTimer) clearTimeout(generationTimer);
    setGenerateButtonState('success');
    setProblemId(status.problem_id);
    setSelectedContext(status.context);
    setSelectedConcepts(status.programming_concepts.split(', '));
    setProblem(status.problem_statement);
    setTest_suite(status.test_driver);
    setConsoleOutput('');
    setIsProblemGenerated(true);
    setIsGeneratingProblem(false);
  };

  const handleProblemStatusPending = () => {
    // Optional: Add any pending state handling here
  };

  const handleProblemStatusError = () => {
    if (generationTimer) clearTimeout(generationTimer);
    setGenerateButtonState('error');
    setIsGeneratingProblem(false);
  };

  const handleGenerateProblem = async () => {
    if (!is_study_participant && (selectedContext === '' || selectedConcepts.length === 0)) {
      setInputWarning('Context and at least one Programming Concept are required!');
      return;
    }

    setInputWarning('');
    if (generationTimer) clearTimeout(generationTimer);
    setGenerateButtonState('generating');
    setIsGeneratingProblem(true);

    try {
      if (user_id === null || session_id === null) {
        throw new Error('Invalid user_id or session_id');
      }
      if (is_study_participant === null) {
        throw new Error('Invalid is_study_participant');
      }
      const response = await HandleProblem.generateProblem(user_id, session_id, is_study_participant, selectedContext, selectedConcepts.join(', '));
      if (response.status === 'completed') {
        if (generationTimer) clearTimeout(generationTimer);
        setProblemId(response.problem_id);
        setSelectedContext(response.context);
        setSelectedConcepts(response.programming_concepts.split(', '));
        setProblem(response.problem_statement);
        setTest_suite(response.test_driver);
        setConsoleOutput('');
        setIsProblemGenerated(true);
        setGenerateButtonState('success');
        setIsGeneratingProblem(false);
      } else if (response.status === 'queued') {
        HandleProblem.pollProblemStatus(
          response.request_id,
          handleProblemStatusSuccess,
          handleProblemStatusPending,
          handleProblemStatusError
        );
      } else {
        if (generationTimer) clearTimeout(generationTimer);
        setGenerateButtonState('error');
        setIsGeneratingProblem(false);
      }
    } catch (error) {
      if (generationTimer) clearTimeout(generationTimer);
      console.error('Error generating problem:', error);
      setGenerateButtonState('error');
      setIsGeneratingProblem(false);
    }
  };

  const handleExecuteCode = async (setOutput: boolean = true) => {
    if (problemId === null) {
      setProblemPlaceholder('Please generate a problem first.');
      return;
    }
    if (setOutput) {
      setIsExecutingCode(true);
      setHasRunCode(true);
    }
    
    try {
      if (user_id === null || session_id === null) {
        throw new Error('Invalid user_id or session_id');
      }
      setTestResults([]);
      const result = await ExecuteCode.execute(user_id, session_id, problemId, code, test_suite);
      
      if (setOutput) {
        setTestResults(result.testResults);
        setConsoleOutput(result.consoleOutput);
        setIsExecutingCode(false);
      }
    } catch (error) {
      if (setOutput) {
        console.error('Error executing code:', error);
        setConsoleOutput(`Error executing code: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setIsExecutingCode(false);
      }
    }
    
  };

  const handleFinish = () => {
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackSubmitted = () => {
    // Reset state variables
    setSelectedContext('');
    setSelectedConcepts([]);
    setProblem('');
    setProblemId(null);
    setCode('# solution_program.py\n# Your Python code here\n\n\n\n\n\n\n\n\n\n\n\n\n');
    setConsoleOutput('');
    setTestResults([]);
    setIsProblemGenerated(false);
    setProblemPlaceholder('A task will be generated in about 2-3 minutes.');
    setGenerateButtonState('idle');
    setHasRunCode(false);
  };

  const {
    feedback,
    feedbackError,
    isFeedbackModalOpen,
    setIsFeedbackModalOpen,
    handleFeedbackChange,
    submitFeedback
  } = useFeedback(user_id, problemId, handleFeedbackSubmitted);

  useEffect(() => {
    return () => {
      if (generationTimer) clearTimeout(generationTimer);
    };
  }, [generationTimer]);

  return (
    <div className="App">
      <div className="color-band">
        <img src="icon.png" alt="Icon" className="icon" />
        <span className="title">PyTaskSyn: Task Synthesis for Python Programming</span>
      </div>
      {user && (
        <div className="user-info">
          {/* <p>Welcome, {user.email}!</p> Display guest user email */}
        </div>
      )}
      <div className="main-container">
        <div className="left-column">
          <div className="input-section">
            <ContextDropdown
              selectedContext={selectedContext}
              setSelectedContext={setSelectedContext}
              disabled={isInputsDisabled}
              is_study_participant={is_study_participant}
            />
            <ConceptsDropdown
              selectedConcepts={selectedConcepts}
              setSelectedConcepts={setSelectedConcepts}
              disabled={isInputsDisabled}
              is_study_participant={is_study_participant}
            />
            {inputWarning && <div className="input-warning" style={{color: 'red', marginBottom: '10px'}}>{inputWarning}</div>}
            <AwesomeButton
              type="primary"
              onPress={handleGenerateProblem}
              disabled={isGeneratingProblem || isProblemGenerated}
              className="generate-problem-btn"
            >
              <span className="button-content">
                <span className="larger-bold-text">
                  {generateButtonState === 'idle' && <><span className="big-icon">🚀</span> Generate Task</>}
                  {generateButtonState === 'generating' && (
                    <>
                      <span className="big-icon">🪄</span> Generating problem 
                      <span className="loading-ellipsis"></span>
                    </>
                  )}
                  {generateButtonState === 'success' && <>Task generated!</>}
                  {generateButtonState === 'error' && <><span className="big-icon">🙁</span> Try again?</>}
                </span>
              </span>
            </AwesomeButton>
          </div>
          <div className="problem-section">
            <h2>Generated Task</h2>
            {problem ? (
              <ReactMarkdown
                components={{
                  code({ node, inline, className, children, ...props }: CodeProps) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={coy as any}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  }
                }}
              >
                {problem}
              </ReactMarkdown>
            ) : (
              <p className="problem-placeholder">{problemPlaceholder}</p>
            )}
          </div>
        </div>
        <div className="right-column">
          <div className="code-section">
            <CodeEditor code={code} setCode={setCode} />
          </div>
          <div className="button-group">
            <AwesomeButton
              type="primary"
              onPress={handleExecuteCode}
              disabled={!isProblemGenerated || isExecutingCode}
              className="action-button run-code-btn"
            >
              <span className="button-content">
                <span className="larger-bold-text">Run Code</span>
              </span>
            </AwesomeButton>
            <AwesomeButton
              type="primary"
              onPress={handleFinish}
              disabled={!hasRunCode}
              className="action-button finish-button"
              cssModule={!hasRunCode ? 'tooltip' : ''}
            >
              <span className="button-content">
                <span className="larger-bold-text">Done</span>
              </span>
            </AwesomeButton>
          </div>
          <div className="test-results-container">
            <TestResultCircles results={testResults} />
          </div>
          <ConsoleOutput output={consoleOutput} isExecuting={isExecutingCode}/>
        </div>
      </div>
      {isFeedbackModalOpen && (
        <div className="modal-overlay">
          <div className="modal-feedback">
            <FeedbackForm
              feedback={feedback}
              onFeedbackChange={handleFeedbackChange}
              onSubmit={submitFeedback}
              error={feedbackError}
              setIsFeedbackModalOpen={setIsFeedbackModalOpen}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;

// export default function AppWithRouter() {
//   return (
//     <Router>
//       <App />
//     </Router>
//   );
// }